import { trackUiEvent } from '../utils/uiAnalytics';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Box, Button, IconButton, Avatar, Paper, Popper } from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon, Language as LanguageIcon } from '@mui/icons-material';
// Global nav sidebar temporarily disabled — restore Drawer + SidebarButton when re-enabled.
// import { Close as CloseIcon } from '@mui/icons-material';
// import { Drawer } from '@mui/material';
import { logout } from '../store/authSlice';
import { RootState, AppDispatch } from '../store';
// import SidebarButton from './new_building/shared/SidebarButton';
import { ProfileDropdownMenu } from './new_login';
import AgentNameCardPage from './bside_page_test/AgentNameCardPage';
import { getSignedInAvatarInitials } from '../services/authService';
import { trackEvent } from '../utils/analytics';
import { AnimatePresence, motion } from 'framer-motion';
import MotionGrow, { useLatchedAnchor } from './motion/MotionGrow';
import { RENDER_EVERY_FRAME, SPRING, delayed, exitTween } from './motion/motionTokens';
import { useStretchEdges } from './motion/useStretchEdges';
import { useDynamicCoachMarkTargetRect } from '../hooks/useDynamicCoachMarkTargetRect';
import {
  GUIDED_LANGUAGE_OPTIONS,
  DEFAULT_GUIDED_LANGUAGE,
  getSmartSearchGuidedCopy,
  normalizeGuidedLanguage,
  type SmartSearchGuidedLanguageCode,
} from './smartsearch/smartSearchGuidedCopy';

/*
interface NavItem {
  text: string;
  path: string;
  icon?: React.ReactNode;
  external?: boolean;
  match?: (pathname: string) => boolean;
}
*/

export interface AppNavbarProps {
  /** Reported from the AppBar so Layout can set `--appbar-height`. */
  onAppBarHeightChange?: (height: number) => void;
  /** Main content (typically `Layout` passes the `<Box component="main">` wrapper). */
  children: React.ReactNode;
}

const NAVBAR_SCALE = {
  xs: 0.62,
  md: 1,
  lg: 1,
} as const;

const toPx = (value: number) => `${Number(value.toFixed(3))}px`;

const scalePx = (base: number) => ({
  xs: toPx(base * NAVBAR_SCALE.xs),
  md: toPx(base * NAVBAR_SCALE.md),
  lg: toPx(base * NAVBAR_SCALE.lg),
});

const NAV_DROPDOWN_CLOSE_DELAY_MS = 0;
const NAV_DROPDOWN_GAP_PX = 8;
const NAV_BAR_PILL_ATTR = 'data-nav-bar-pill';

/** Visible gap below the nav pill. The trigger sits inside the pill, so we add
 *  the leftover distance to the pill's bottom or the menu overlays the bar. */
const getDropdownBridgePx = (anchor: Element | null, visibleGapPx: number): number => {
  if (!anchor) return visibleGapPx;
  const pill = anchor.closest(`[${NAV_BAR_PILL_ATTR}]`);
  if (!pill) return visibleGapPx;
  const extra = pill.getBoundingClientRect().bottom - anchor.getBoundingClientRect().bottom;
  return Math.max(0, extra) + visibleGapPx;
};
const NAV_DROPDOWN_WIDTH_PX = 290;
const NAV_DROPDOWN_PADDING_X_PX = 10;
const NAV_DROPDOWN_PADDING_Y_PX = 20;
const NAV_DROPDOWN_ITEM_GAP_PX = 20;
// Shared panel's closed shape: a label-wide strip just below the bar.
const NAV_DROPDOWN_CLOSED_HEIGHT_PX = 24;
const NAV_DROPDOWN_VIEWPORT_MARGIN_PX = 8;
// Roughly when SPRING.morph has visually settled.
const NAV_DROPDOWN_SETTLE_MS = 450;
const NAV_HOVER_PILL_HEIGHT_PX = 36;
const NAV_HOVER_PILL_PADDING_X_PX = 16;
const NAV_FULL_MAX_WIDTH_PX = 1483;
const NAV_SHRUNK_MAX_WIDTH_PX = 640;
const NAV_SHRUNK_LANGUAGE_WIDTH_PX = 54;
const NAV_SHRUNK_GUEST_LOGIN_WIDTH_PX = 90;
const BSIDE_NAV_MAX_WIDTH_PX = 1483;
const LANGUAGE_SELECTOR_TUTORIAL_VERSION = 1;
const LANGUAGE_SELECTOR_TUTORIAL_STORAGE_PREFIX = 'smartSearchLanguageSelectorTutorialSeen';
export const CUSTOMER_COMPARE_NAV_LABEL = 'Compare & Review';

type NavDropdownId = 'compare' | 'my-journey' | 'bside-offer';
type BsideNavItemId = 'bside-dashboard' | 'bside-schedule' | 'bside-listing' | 'bside-offer' | 'bside-smart-customer' | 'bside-nexa-me';
type NavItemId = 'smart-search' | NavDropdownId | BsideNavItemId;
type NavItemAnchors = Partial<Record<NavItemId, HTMLElement | null>>;

// Left-to-right order, so moving between dropdowns slides the panel content the same way.
const NAV_DROPDOWN_ORDER: NavDropdownId[] = ['compare', 'my-journey', 'bside-offer'];
// Customer links that get the sliding hover pill instead of the grow/bold hover.
const NAV_HOVER_PILL_ITEMS: NavItemId[] = ['smart-search', 'compare', 'my-journey'];

type BsideNavItemDef = {
  id: BsideNavItemId;
  label: string;
  path: string;
  hasDropdown?: boolean;
  match: (pathname: string) => boolean;
};

export const BSIDE_NAV_ITEMS: BsideNavItemDef[] = [
  {
    id: 'bside-dashboard',
    label: 'Dashboard',
    path: '/business/dashboard',
    match: pathname => pathname.startsWith('/business/dashboard'),
  },
  {
    id: 'bside-schedule',
    label: 'Schedule',
    path: '/business/schedule',
    match: pathname => pathname.startsWith('/business/schedule'),
  },
  {
    id: 'bside-listing',
    label: 'Listing',
    path: '/business/listing',
    match: pathname => pathname.startsWith('/business/listing'),
  },
  // Offer is excluded from this release; retain the entry for later restoration.
  // {
  // id: 'bside-offer',
  // label: 'Offer',
  // path: '/business/offer',
  // hasDropdown: true,
  // match: pathname => pathname.startsWith('/business/offer'),
  // },
  {
    id: 'bside-smart-customer',
    label: 'Smart Customer',
    path: '/business/smart-customer',
    match: pathname => pathname.startsWith('/business/smart-customer'),
  },
  {
    id: 'bside-nexa-me',
    label: 'Nexa Me',
    path: '/business/nexa-me',
    match: pathname => pathname.startsWith('/business/nexa-me'),
  },
];

export const isBsideNavItemActive = (item: BsideNavItemDef, pathname: string): boolean => item.match(pathname);

export const getSignedInSwitchAccountConfig = (isBsideMode: boolean) => (
  isBsideMode
    ? { label: 'Sign in as renter', targetPath: '/login' }
    : { label: 'Sign in as agent', targetPath: '/business/login' }
);

export const getBrandNavigateTarget = (isBsideMode: boolean) => (
  isBsideMode ? '/business/dashboard' : '/'
);

const getNavItemDimSx = (itemId: NavItemId, focusedNavItem: NavItemId | null) => ({
  transition: 'filter 180ms ease, opacity 180ms ease',
  ...(focusedNavItem && focusedNavItem !== itemId
    ? {
        filter: 'blur(2px)',
        opacity: 0.9,
      }
    : {}),
});

const buildLanguageSelectorTutorialStorageKey = (userId?: string | number | null): string => (
  `${LANGUAGE_SELECTOR_TUTORIAL_STORAGE_PREFIX}:${String(userId || 'anonymous')}:v${LANGUAGE_SELECTOR_TUTORIAL_VERSION}`
);

const hasSeenLanguageSelectorTutorial = (userId?: string | number | null): boolean => {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(buildLanguageSelectorTutorialStorageKey(userId)) === 'true';
  } catch {
    return true;
  }
};

const markLanguageSelectorTutorialSeen = (userId?: string | number | null): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(buildLanguageSelectorTutorialStorageKey(userId), 'true');
  } catch {
    // Non-critical UI hint state. Ignore unavailable storage.
  }
};

const shouldShowLanguageSelectorTutorial = ({
  showGuidedLanguageGlobe,
  isMobileViewport,
  hasSeenTutorial,
}: {
  showGuidedLanguageGlobe: boolean;
  isMobileViewport: boolean;
  hasSeenTutorial: boolean;
}): boolean => showGuidedLanguageGlobe && !isMobileViewport && !hasSeenTutorial;

export const shouldShowLanguageSelectorTutorialForTest = shouldShowLanguageSelectorTutorial;

const shouldShowCustomerWordmark = ({
  isBsideMode,
  isNavShrunk,
  isMobileViewport,
}: {
  isBsideMode: boolean;
  isNavShrunk: boolean;
  isMobileViewport: boolean;
}): boolean => !isBsideMode && !isNavShrunk && !isMobileViewport;

export const shouldShowCustomerWordmarkForTest = shouldShowCustomerWordmark;

export const shouldShowGuestSmartSearchCtaForTest = ({
  isChatRoute,
  isFocusedMode,
  isAuthenticated,
  isNavShrunk,
}: {
  isChatRoute: boolean;
  isFocusedMode: boolean;
  isAuthenticated: boolean;
  isNavShrunk: boolean;
}): boolean => isChatRoute && isFocusedMode && !isAuthenticated && !isNavShrunk;

const shouldShowGuestLoginButton = ({
  isAuthenticated,
}: {
  isAuthenticated: boolean;
}): boolean => !isAuthenticated;

export const shouldShowGuestLoginButtonForTest = shouldShowGuestLoginButton;

const getCustomerNavMaxWidth = ({
  isNavShrunk,
  showGuidedLanguageGlobe,
  showGuestLoginButton,
}: {
  isNavShrunk: boolean;
  showGuidedLanguageGlobe: boolean;
  showGuestLoginButton: boolean;
}): number => {
  if (!isNavShrunk) return NAV_FULL_MAX_WIDTH_PX;
  return NAV_SHRUNK_MAX_WIDTH_PX
    + (showGuidedLanguageGlobe ? NAV_SHRUNK_LANGUAGE_WIDTH_PX : 0)
    + (showGuestLoginButton ? NAV_SHRUNK_GUEST_LOGIN_WIDTH_PX : 0);
};

export const getCustomerNavMaxWidthForTest = getCustomerNavMaxWidth;

export const resolveSignedInAvatarInitials = getSignedInAvatarInitials;

const getGuestLoginButtonSizing = (isNavShrunk: boolean) => ({
  width: isNavShrunk ? { xs: '104px', md: '128px' } : { xs: '128px', md: '195px' },
  minWidth: isNavShrunk ? { xs: '104px', md: '128px' } : { xs: '128px', md: '195px' },
  height: isNavShrunk ? '30px' : '38px',
  fontSize: isNavShrunk ? { xs: '12px', md: '14px' } : { xs: '14px', md: '18px' },
  px: isNavShrunk ? { xs: '8px', md: '14px' } : { xs: '10px', md: '5px' },
});

export const getGuestLoginButtonSizingForTest = getGuestLoginButtonSizing;

// The label is always laid out at the full-size font and shrunk with a transform, so the shrink
// is composited instead of re-laying out the text every frame; the scale lands it on the
// compact font size from getGuestLoginButtonSizing.
const GUEST_LOGIN_FULL_FONT = getGuestLoginButtonSizing(false).fontSize;
const GUEST_LOGIN_COMPACT_FONT = getGuestLoginButtonSizing(true).fontSize;
const guestLoginLabelScale = (breakpoint: 'xs' | 'md') =>
  parseFloat(GUEST_LOGIN_COMPACT_FONT[breakpoint]) / parseFloat(GUEST_LOGIN_FULL_FONT[breakpoint]);
const getGuestLoginLabelSx = (isNavShrunk: boolean) => ({
  display: 'inline-block',
  transformOrigin: 'center',
  transform: isNavShrunk
    ? { xs: `scale(${guestLoginLabelScale('xs')})`, md: `scale(${guestLoginLabelScale('md')})` }
    : 'none',
  transition: 'transform 360ms cubic-bezier(0.22, 1, 0.36, 1)',
});

type NavDropdownItemDef = {
  label: string;
  onClick: () => void;
  isActive: boolean;
};

const navLinkLabelDefaultTypographySx = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 400,
  fontSize: scalePx(18),
  fontStyle: 'normal' as const,
  lineHeight: 'normal' as const,
  textAlign: 'center' as const,
};

const navLinkLabelHoverTypographySx = {
  fontWeight: 700,
  fontSize: scalePx(19),
  fontStyle: 'normal' as const,
  lineHeight: 'normal' as const,
  textAlign: 'center' as const,
};

const navLinkLabelTypographyTransitionSx = {
  transition:
    'font-size 180ms cubic-bezier(0.2, 0.8, 0.2, 1), font-weight 180ms cubic-bezier(0.2, 0.8, 0.2, 1)',
};

const navLinkHoverMotionSx = {
  transform: 'translateY(0) scale(1)',
  transformOrigin: 'center',
  transition: 'transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  willChange: 'transform',
  '&:hover, &:focus-within': {
    transform: 'translateY(-1px) scale(1.045)',
  },
  '&:hover .nav-bar-link-label, &:focus-within .nav-bar-link-label': {
    ...navLinkLabelHoverTypographySx,
  },
};

const navLinkLabelButtonSx = {
  p: 0,
  minWidth: 'auto',
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
  ...navLinkLabelDefaultTypographySx,
  ...navLinkLabelTypographyTransitionSx,
  color: '#363434',
  backgroundColor: 'transparent',
  '&:hover, &:focus-visible': {
    backgroundColor: 'transparent',
  },
};

interface NavHoverDropdownProps {
  dropdownId: NavDropdownId;
  navItemId: NavDropdownId;
  focusedNavItem: NavItemId | null;
  open: boolean;
  onDropdownEnter: (id: NavDropdownId) => void;
  anchorRef: (el: HTMLElement | null) => void;
  label: string;
  onLabelClick: () => void;
  /** B-side keeps the grow/bold hover; customer links use the shared hover pill. */
  withHoverMotion?: boolean;
}

// Trigger only: the panel itself is the shared `NavDropdownPanel`, so moving between two
// dropdown labels slides one panel instead of closing one and popping another. Closing is
// owned by the links row and the panel (leaving either), not by the label.
const NavHoverDropdown: React.FC<NavHoverDropdownProps> = ({
  dropdownId,
  navItemId,
  focusedNavItem,
  open,
  onDropdownEnter,
  anchorRef,
  label,
  onLabelClick,
  withHoverMotion = false,
}) => (
  <Box
    ref={anchorRef}
    onMouseEnter={() => onDropdownEnter(dropdownId)}
    sx={{
      display: 'flex',
      alignItems: 'center',
      ...(withHoverMotion ? navLinkHoverMotionSx : {}),
      ...getNavItemDimSx(navItemId, focusedNavItem),
    }}
  >
    <Button
      onClick={onLabelClick}
      disableRipple
      className="nav-bar-link-label"
      sx={navLinkLabelButtonSx}
    >
      {label}
    </Button>
    <KeyboardArrowDownIcon
      aria-hidden
      sx={{
        ml: scalePx(1),
        width: scalePx(18),
        height: scalePx(18),
        color: '#363434',
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 220ms ease-in-out',
      }}
    />
  </Box>
);

type NavDropdownGeometry = {
  id: NavDropdownId;
  /** Open panel's left edge: centered on the label, clamped to the viewport. */
  left: number;
  labelLeft: number;
  labelWidth: number;
  /** Panel top: the nav pill's bottom plus the visible gap. */
  top: number;
  /** Hover bridge top: the label's bottom, so the cursor can travel into the panel. */
  bridgeTop: number;
  contentHeight: number | null;
  measuredOpen: boolean;
  /** Re-measure of the same, settled menu (resize, shrink): snap, don't animate. */
  layoutOnly: boolean;
};

interface NavDropdownPanelProps {
  openId: NavDropdownId | null;
  menus: Partial<Record<NavDropdownId, NavDropdownItemDef[]>>;
  anchorsRef: React.MutableRefObject<NavItemAnchors>;
  /** Positioned ancestor the panel is laid out in (the nav wrapper, outside the pill's overflow). */
  frameRef: React.RefObject<HTMLElement>;
  layoutKey: unknown;
  onDropdownEnter: (id: NavDropdownId) => void;
  onLeave: () => void;
  onItemSelect: () => void;
}

const navDropdownSwapVariants = {
  enter: (direction: number) => ({ opacity: 0, filter: 'blur(6px)', x: direction * 24 }),
  get center() {
    return { opacity: 1, filter: 'blur(0px)', x: 0, transition: delayed(SPRING.snap, 0.04) };
  },
  exit: (direction: number) => ({ opacity: 0, filter: 'blur(6px)', x: direction * -24, transition: exitTween(0.12) }),
};

// One glass panel for every hover dropdown. It grows down from just below the bar starting
// at the label's width, slides/resizes between labels (items blur across in the travel
// direction) and shrinks back toward the label on close. It lives below the pill, never on it.
const NavDropdownPanel: React.FC<NavDropdownPanelProps> = ({
  openId,
  menus,
  anchorsRef,
  frameRef,
  layoutKey,
  onDropdownEnter,
  onLeave,
  onItemSelect,
}) => {
  const latchedId = useLatchedAnchor(openId);
  const [geometry, setGeometry] = useState<NavDropdownGeometry | null>(null);
  const [mounted, setMounted] = useState(false);
  const [swap, setSwap] = useState<{ id: NavDropdownId | null; direction: number }>({ id: openId, direction: 0 });
  const contentNodesRef = useRef<Partial<Record<NavDropdownId, HTMLDivElement | null>>>({});
  const motionKeyRef = useRef('');
  const motionStartedAtRef = useRef(0);

  if (openId && !mounted) setMounted(true);
  let direction = swap.direction;
  if (swap.id !== openId) {
    const from = swap.id;
    direction = from && openId && from !== openId
      ? (NAV_DROPDOWN_ORDER.indexOf(openId) > NAV_DROPDOWN_ORDER.indexOf(from) ? 1 : -1)
      : 0;
    setSwap({ id: openId, direction });
  }

  const measure = useCallback(() => {
    const anchor = latchedId ? anchorsRef.current[latchedId] : null;
    const frame = frameRef.current;
    if (!latchedId || !anchor || !frame) return;
    const pill = anchor.closest(`[${NAV_BAR_PILL_ATTR}]`);
    const a = anchor.getBoundingClientRect();
    const f = frame.getBoundingClientRect();
    const pillBottom = pill ? pill.getBoundingClientRect().bottom : a.bottom;
    const maxLeft = window.innerWidth - NAV_DROPDOWN_VIEWPORT_MARGIN_PX - NAV_DROPDOWN_WIDTH_PX;
    const viewportLeft = Math.max(
      NAV_DROPDOWN_VIEWPORT_MARGIN_PX,
      Math.min(a.left + a.width / 2 - NAV_DROPDOWN_WIDTH_PX / 2, maxLeft),
    );
    const content = contentNodesRef.current[latchedId];
    // Re-measures while an open/switch spring is still running retarget it; once it has
    // settled, a moved bar (shrink, resize) snaps the panel along instead of trailing it.
    const motionKey = `${latchedId}:${Boolean(openId)}`;
    if (motionKeyRef.current !== motionKey) {
      motionKeyRef.current = motionKey;
      motionStartedAtRef.current = performance.now();
    }
    const settled = performance.now() - motionStartedAtRef.current > NAV_DROPDOWN_SETTLE_MS;
    setGeometry(prev => {
      const next: NavDropdownGeometry = {
        id: latchedId,
        // Whole pixels: sub-pixel drift from in-flight CSS transitions must not count as a change.
        left: Math.round(viewportLeft - f.left),
        labelLeft: Math.round(a.left - f.left),
        labelWidth: Math.round(a.width),
        top: Math.round(pillBottom + NAV_DROPDOWN_GAP_PX - f.top),
        bridgeTop: Math.round(a.bottom - f.top),
        contentHeight: content ? content.offsetHeight : (prev?.id === latchedId ? prev.contentHeight : null),
        measuredOpen: Boolean(openId),
        layoutOnly: settled && prev?.id === latchedId && prev.measuredOpen === Boolean(openId),
      };
      const unchanged = prev
        && prev.id === next.id
        && prev.left === next.left
        && prev.labelLeft === next.labelLeft
        && prev.labelWidth === next.labelWidth
        && prev.top === next.top
        && prev.bridgeTop === next.bridgeTop
        && prev.contentHeight === next.contentHeight
        && prev.measuredOpen === next.measuredOpen;
      return unchanged ? prev : next;
    });
  }, [anchorsRef, frameRef, latchedId, openId]);

  // Re-measure when the open menu changes (its content mounts in the same commit). Not after
  // every render: while the bar is mid shrink-transition each read returns a slightly different
  // rect, so an every-render measure re-renders itself forever (max update depth on /chat).
  useLayoutEffect(() => {
    if (openId) measure();
  }, [openId, measure]);
  useLayoutEffect(() => {
    if (mounted) measure();
  }, [layoutKey, mounted, measure]);

  // Follow the pill while it resizes (shrink/expand transitions, wordmark, window resize).
  useEffect(() => {
    if (!mounted || !latchedId) return undefined;
    const anchor = anchorsRef.current[latchedId];
    window.addEventListener('resize', measure);
    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(() => measure());
    [frameRef.current, anchor?.closest(`[${NAV_BAR_PILL_ATTR}]`), anchor?.parentElement].forEach(el => {
      if (el) observer.observe(el);
    });
    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [anchorsRef, frameRef, latchedId, measure, mounted]);

  if (!mounted || !geometry) return null;

  const open = Boolean(openId);
  const snapPosition = geometry.layoutOnly && geometry.measuredOpen === open;
  const closedTarget = {
    left: geometry.labelLeft,
    width: geometry.labelWidth,
    height: NAV_DROPDOWN_CLOSED_HEIGHT_PX,
    opacity: 0,
    y: -6,
  };
  const openTarget = {
    left: geometry.left,
    width: NAV_DROPDOWN_WIDTH_PX,
    // +1 for the 0.5px border on each side (border-box).
    height: geometry.contentHeight != null ? geometry.contentHeight + 1 : NAV_DROPDOWN_CLOSED_HEIGHT_PX,
    opacity: 1,
    y: 0,
  };
  const items = openId ? menus[openId] ?? [] : [];

  return (
    <>
      {openId ? (
        // Transparent bridge from the label down to the panel keeps it open while the cursor
        // travels across the pill's padding and the visible gap.
        <Box
          aria-hidden
          onMouseEnter={() => onDropdownEnter(openId)}
          onMouseLeave={onLeave}
          sx={{
            position: 'absolute',
            zIndex: 1,
            top: geometry.bridgeTop,
            left: geometry.left,
            width: NAV_DROPDOWN_WIDTH_PX,
            height: Math.max(0, geometry.top - geometry.bridgeTop) + 2,
          }}
        />
      ) : null}
      <motion.div
        {...RENDER_EVERY_FRAME}
        initial={closedTarget}
        animate={open ? openTarget : closedTarget}
        transition={{
          ...SPRING.morph,
          ...(snapPosition ? { left: { duration: 0 }, width: { duration: 0 } } : {}),
          opacity: open ? { duration: 0.12 } : exitTween(0.14),
        }}
        onAnimationComplete={definition => {
          if (!openId && (definition as { opacity?: number }).opacity === 0) {
            setMounted(false);
            setGeometry(null);
          }
        }}
        onMouseEnter={openId ? () => onDropdownEnter(openId) : undefined}
        onMouseLeave={onLeave}
        style={{
          position: 'absolute',
          zIndex: 1,
          top: geometry.top,
          boxSizing: 'border-box',
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(211, 204, 201, 0.2)',
          border: '0.5px solid #D3CCC9',
          borderRadius: '20px',
          boxShadow: '0px 4px 6px 0px rgba(0,0,0,0.05)',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <AnimatePresence custom={direction}>
          {openId ? (
            <motion.div
              {...RENDER_EVERY_FRAME}
              key={openId}
              ref={node => { contentNodesRef.current[openId] = node; }}
              custom={direction}
              variants={navDropdownSwapVariants}
              initial="enter"
              animate="center"
              exit="exit"
              style={{ position: 'absolute', top: 0, left: 0, width: NAV_DROPDOWN_WIDTH_PX - 1 }}
            >
              <Box
                sx={{
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: `${NAV_DROPDOWN_PADDING_Y_PX}px`,
                  px: `${NAV_DROPDOWN_PADDING_X_PX}px`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: `${NAV_DROPDOWN_ITEM_GAP_PX}px`,
                    alignItems: 'stretch',
                    width: '100%',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    lineHeight: 'normal',
                  }}
                >
                  {items.map((item, index) => (
                    <motion.div
                      {...RENDER_EVERY_FRAME}
                      key={item.label}
                      // Items stagger in on a fresh open; on a switch the whole block slides.
                      initial={direction === 0 ? { opacity: 0, filter: 'blur(4px)', y: -4 } : false}
                      animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
                      transition={delayed(SPRING.snap, 0.06 + index * 0.03)}
                      style={{ display: 'flex' }}
                    >
                      <Button
                        onClick={() => {
                          onItemSelect();
                          item.onClick();
                        }}
                        disableRipple
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          width: '100%',
                          justifyContent: 'center',
                          textTransform: 'none',
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '14px',
                          fontWeight: item.isActive ? 700 : 400,
                          lineHeight: 'normal',
                          color: item.isActive ? '#e15600' : '#363434',
                          cursor: 'pointer',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          '&:hover, &:focus-visible': {
                            backgroundColor: 'transparent',
                            color: '#e15600',
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

type NavHoverPillRect = { left: number; right: number; centerY: number; height: number };

const NavHoverPillShape: React.FC<{ rect: NavHoverPillRect; visible: boolean; moveKey: string }> = ({
  rect,
  visible,
  moveKey,
}) => {
  const { left, width } = useStretchEdges(
    rect.left - NAV_HOVER_PILL_PADDING_X_PX,
    rect.right + NAV_HOVER_PILL_PADDING_X_PX,
    moveKey,
  );
  return (
    <motion.div
      {...RENDER_EVERY_FRAME}
      aria-hidden
      initial={{ opacity: 0 }}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={SPRING.snap}
      style={{
        position: 'absolute',
        // Below the labels: the pill box is a stacking context (backdrop-filter), so -1
        // paints over its glass but under its in-flow content.
        zIndex: -1,
        top: rect.centerY - rect.height / 2,
        height: rect.height,
        left,
        width,
        borderRadius: 999,
        background: 'rgba(54, 52, 52, 0.06)',
        pointerEvents: 'none',
      }}
    />
  );
};

// One soft pill that slides between the hovered customer links (no size/weight change, so
// nothing reflows). Rendered inside the nav pill; hidden when nothing is hovered.
const NavHoverPill: React.FC<{
  hoveredId: NavItemId | null;
  anchorsRef: React.MutableRefObject<NavItemAnchors>;
  layoutKey: unknown;
}> = ({ hoveredId, anchorsRef, layoutKey }) => {
  const target = hoveredId && NAV_HOVER_PILL_ITEMS.includes(hoveredId) ? hoveredId : null;
  const latchedId = useLatchedAnchor(target);
  const [rect, setRect] = useState<NavHoverPillRect | null>(null);

  const measure = useCallback(() => {
    const el = latchedId ? anchorsRef.current[latchedId] : null;
    const pill = el?.closest<HTMLElement>(`[${NAV_BAR_PILL_ATTR}]`);
    if (!el || !pill) return;
    const r = el.getBoundingClientRect();
    const p = pill.getBoundingClientRect();
    const next = {
      left: Math.round(r.left - p.left - pill.clientLeft),
      right: Math.round(r.right - p.left - pill.clientLeft),
      centerY: Math.round((r.top + r.bottom) / 2 - p.top - pill.clientTop),
      height: Math.min(NAV_HOVER_PILL_HEIGHT_PX, pill.clientHeight),
    };
    setRect(prev => (
      prev
      && prev.left === next.left
      && prev.right === next.right
      && prev.centerY === next.centerY
      && prev.height === next.height
        ? prev
        : next
    ));
  }, [anchorsRef, latchedId]);

  useLayoutEffect(() => {
    measure();
  }, [layoutKey, measure]);

  useEffect(() => {
    if (!target) return undefined;
    const el = anchorsRef.current[target];
    window.addEventListener('resize', measure);
    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(() => measure());
    [el?.closest(`[${NAV_BAR_PILL_ATTR}]`), el?.parentElement].forEach(node => {
      if (node) observer.observe(node);
    });
    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [anchorsRef, measure, target]);

  if (!rect) return null;
  return <NavHoverPillShape rect={rect} visible={Boolean(target)} moveKey={target ?? 'none'} />;
};

const AppNavbar: React.FC<AppNavbarProps> = ({ onAppBarHeightChange, children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated, profile } = useSelector((state: RootState) => state.auth);
  // const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [showNameCardOverlay, setShowNameCardOverlay] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isBsideRoute = location.pathname.startsWith('/business');
  const isBsideUser = Boolean(isAuthenticated && (user?.role === 'BROKER' || user?.clientType === 'BROKER'));
  const isBsideMode = isBsideRoute || isBsideUser;
  const activeProfileMenuItem =
    showNameCardOverlay || location.pathname === '/business/name-card'
      ? 'name-card'
      : location.pathname === '/business/settings' || location.pathname === '/settings'
      ? 'setting'
      : null;

  const isSearchRoute = location.pathname.startsWith('/chat-with-mimi');
  const isLandingRoute = location.pathname === '/';
  const [isSmartSearchChatMode, setIsSmartSearchChatMode] = useState(() => (
    typeof document !== 'undefined' && document.body.dataset.smartSearchChatMode === 'true'
  ));
  const [isSmartSearchFocusedMode, setIsSmartSearchFocusedMode] = useState(() => (
    typeof document !== 'undefined' && document.body.dataset.smartSearchFocusedMode === 'true'
  ));
  const [isSmartSearchGuidedMode, setIsSmartSearchGuidedMode] = useState(() =>
    typeof document !== 'undefined' && document.body.dataset.smartSearchGuidedMode === 'true'
  );
  const isLoginRoute = location.pathname === '/login' || location.pathname === '/business/login';
  const isSearchLandingRoute = isSearchRoute && !isSmartSearchChatMode;
  // /chat-with-mimi landing (Past Review) keeps auto-hide-on-idle reveal. Home only shrinks — no auto-hide.
  const isNavSmartRevealActive = !isLoginRoute && isSearchLandingRoute;
  // Chat page (Smart Search): the bar floats and stays visible. It only collapses into
  // a centered pill (Wing-style) once the user scrolls down OR expands the profile panel,
  // so the user sees the full bar first and then the shrink animation.
  const isChatRoute = !isLoginRoute && isSearchRoute && isSmartSearchChatMode;
  const showGuidedLanguageGlobe = isChatRoute && isSmartSearchGuidedMode;
  const isNavFixed = isNavSmartRevealActive || isChatRoute || isLandingRoute;
  const [isChatPageScrolled, setIsChatPageScrolled] = useState(false);
  const [isHomePageScrolled, setIsHomePageScrolled] = useState(false);
  const [isProfilePanelExpanded, setIsProfilePanelExpanded] = useState(false);
  const [isLandingIntroVisible, setIsLandingIntroVisible] = useState(() => {
    if (typeof document === 'undefined') return false;
    return isLandingRoute && document.body.dataset.landingIntroVisible !== 'false';
  });
  // Focused pre-chat has no profile panel / chat scroller, so keep the full bar
  // (no shrink-to-pill) while still allowing idle hide + upward-gesture reveal.
  const isChatShrunk = isChatRoute
    && !isSmartSearchFocusedMode
    && (isChatPageScrolled || isProfilePanelExpanded);
  const isFocusedPreChatAutoHide = isChatRoute && isSmartSearchFocusedMode;
  const isNavShrunk = isChatShrunk || (isLandingRoute && isHomePageScrolled);
  const showGuestLoginButton = shouldShowGuestLoginButton({ isAuthenticated });
  // Auto-hide on /chat-with-mimi landing, chat-after-shrink, and focused pre-chat idle.
  const isNavAutoHide = isNavSmartRevealActive || isChatShrunk || isFocusedPreChatAutoHide;
  const navHideTimerRef = useRef<number | null>(null);
  const isMenuOpenRef = useRef(false);
  const isNavHoveredRef = useRef(false);
  const openNavDropdownRef = useRef<NavDropdownId | null>(null);
  const isLangMenuOpenRef = useRef(false);
  const [isNavRevealed, setIsNavRevealed] = useState(() => !isNavSmartRevealActive);
  const revealRafRef = useRef<number | null>(null);
  const appBarRef = useRef<HTMLDivElement | null>(null);
  const navFrameRef = useRef<HTMLDivElement | null>(null);
  // Nav link elements, read by the shared dropdown panel and the hover pill.
  const navItemAnchorsRef = useRef<NavItemAnchors>({});
  const navItemAnchorRefCallbacksRef = useRef<Partial<Record<NavItemId, (el: HTMLElement | null) => void>>>({});
  const getNavItemAnchorRef = useCallback((id: NavItemId) => {
    if (!navItemAnchorRefCallbacksRef.current[id]) {
      navItemAnchorRefCallbacksRef.current[id] = (el: HTMLElement | null) => {
        navItemAnchorsRef.current[id] = el;
      };
    }
    return navItemAnchorRefCallbacksRef.current[id]!;
  }, []);

  // Language globe (route-aware: only the /chat-with-mimi Smart Search chat shows it). The selected
  // language is synced with SmartSearchLanding via window events + a body dataset hint.
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langAnchorRef = useRef<HTMLDivElement | null>(null);
  const [langBridgePx, setLangBridgePx] = useState(NAV_DROPDOWN_GAP_PX);
  const langCloseTimerRef = useRef<number | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<SmartSearchGuidedLanguageCode>(() =>
    normalizeGuidedLanguage(
      typeof document !== 'undefined' ? document.body.dataset.smartSearchLanguage : DEFAULT_GUIDED_LANGUAGE
    )
  );
  const [isLanguageSelectorTutorialMobileViewport, setIsLanguageSelectorTutorialMobileViewport] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 899.95px)').matches
  ));
  const [hasSeenLanguageSelectorCoachMark, setHasSeenLanguageSelectorCoachMark] = useState(true);
  isLangMenuOpenRef.current = isLangMenuOpen;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 899.95px)');
    const sync = () => setIsLanguageSelectorTutorialMobileViewport(media.matches);
    sync();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', sync);
      return () => media.removeEventListener('change', sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  useEffect(() => {
    const readDataset = () => document.body.dataset.smartSearchChatMode === 'true';
    const sync = (enabled?: boolean) => {
      if (!isSearchRoute) {
        setIsSmartSearchChatMode(false);
        return;
      }
      setIsSmartSearchChatMode(Boolean(enabled ?? readDataset()));
    };

    sync();
    const handler = (event: Event) => {
      const ce = event as CustomEvent<{ enabled?: boolean }>;
      sync(ce.detail?.enabled);
    };
    window.addEventListener('smartsearch:chatmode', handler as EventListener);
    return () => {
      window.removeEventListener('smartsearch:chatmode', handler as EventListener);
    };
  }, [isSearchRoute]);

  useEffect(() => {
    const readDataset = () => document.body.dataset.smartSearchFocusedMode === 'true';
    const sync = (enabled?: boolean) => {
      if (!isSearchRoute) {
        setIsSmartSearchFocusedMode(false);
        return;
      }
      setIsSmartSearchFocusedMode(Boolean(enabled ?? readDataset()));
    };

    sync();
    const handler = (event: Event) => {
      const ce = event as CustomEvent<{ enabled?: boolean }>;
      sync(ce.detail?.enabled);
    };
    window.addEventListener('smartsearch:focusedmode', handler as EventListener);
    return () => {
      window.removeEventListener('smartsearch:focusedmode', handler as EventListener);
    };
  }, [isSearchRoute]);

  useEffect(() => {
    const readDataset = () => document.body.dataset.smartSearchGuidedMode === 'true';
    const sync = (enabled?: boolean) => {
      if (!isSearchRoute) {
        setIsSmartSearchGuidedMode(false);
        return;
      }
      setIsSmartSearchGuidedMode(Boolean(enabled ?? readDataset()));
    };

    sync();
    const handler = (event: Event) => {
      const ce = event as CustomEvent<{ enabled?: boolean }>;
      sync(ce.detail?.enabled);
    };
    window.addEventListener('smartsearch:guidedmode', handler as EventListener);
    return () => {
      window.removeEventListener('smartsearch:guidedmode', handler as EventListener);
    };
  }, [isSearchRoute]);

  useEffect(() => {
    const readDataset = () => document.body.dataset.landingIntroVisible !== 'false';
    const sync = (visible?: boolean) => {
      if (!isLandingRoute) {
        setIsLandingIntroVisible(false);
        return;
      }
      setIsLandingIntroVisible(Boolean(visible ?? readDataset()));
    };

    sync();
    const handler = (event: Event) => {
      const ce = event as CustomEvent<{ visible?: boolean }>;
      sync(ce.detail?.visible);
    };
    window.addEventListener('landing:intro-visible', handler as EventListener);
    return () => {
      window.removeEventListener('landing:intro-visible', handler as EventListener);
    };
  }, [isLandingRoute]);

  useLayoutEffect(() => {
    const measure = () => {
      if (!appBarRef.current) return;
      const h = appBarRef.current.getBoundingClientRect().height;
      if (h > 0) onAppBarHeightChange?.(h);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [onAppBarHeightChange]);

  const NAV_IDLE_HIDE_MS = 1400;
  const clearNavHideTimer = useCallback(() => {
    if (navHideTimerRef.current != null) {
      window.clearTimeout(navHideTimerRef.current);
      navHideTimerRef.current = null;
    }
  }, []);
  const scheduleHideNavAfterIdle = useCallback(() => {
    if (!isNavAutoHide) return;
    if (isMenuOpenRef.current) return;
    if (isNavHoveredRef.current) return;
    if (openNavDropdownRef.current) return;
    if (isLangMenuOpenRef.current) return;
    clearNavHideTimer();
    navHideTimerRef.current = window.setTimeout(() => {
      navHideTimerRef.current = null;
      if (
        !isMenuOpenRef.current
        && !isNavHoveredRef.current
        && !openNavDropdownRef.current
        && !isLangMenuOpenRef.current
      ) {
        setIsNavRevealed(false);
      }
    }, NAV_IDLE_HIDE_MS);
  }, [clearNavHideTimer, isNavAutoHide]);
  const handleNavMouseEnter = useCallback(() => {
    if (!isNavAutoHide) return;
    isNavHoveredRef.current = true;
    clearNavHideTimer();
    setIsNavRevealed(true);
  }, [clearNavHideTimer, isNavAutoHide]);
  const handleNavMouseLeave = useCallback(() => {
    if (!isNavAutoHide) return;
    isNavHoveredRef.current = false;
    if (!isMenuOpenRef.current && !openNavDropdownRef.current && !isLangMenuOpenRef.current) {
      scheduleHideNavAfterIdle();
    }
  }, [isNavAutoHide, scheduleHideNavAfterIdle]);
  const revealNav = useCallback(() => {
    if (!isNavAutoHide) return;
    setIsNavRevealed(true);
    scheduleHideNavAfterIdle();
  }, [isNavAutoHide, scheduleHideNavAfterIdle]);

  useEffect(() => {
    const wasOpen = isMenuOpenRef.current;
    const isOpen = userMenuAnchor !== null;
    isMenuOpenRef.current = isOpen;

    if (wasOpen && !isOpen && isNavAutoHide) {
      scheduleHideNavAfterIdle();
    }
  }, [userMenuAnchor, isNavAutoHide, scheduleHideNavAfterIdle]);

  // Reflect the language SmartSearchLanding is currently using.
  useEffect(() => {
    const handler = (event: Event) => {
      const code = (event as CustomEvent<{ code?: string }>).detail?.code;
      if (code) setSelectedLanguage(normalizeGuidedLanguage(code));
    };
    window.addEventListener('smartsearch:language', handler as EventListener);
    return () => window.removeEventListener('smartsearch:language', handler as EventListener);
  }, []);

  // Keep the bar revealed while the language menu is open; resume auto-hide once it closes.
  useEffect(() => {
    if (!isNavAutoHide) return;
    if (isLangMenuOpen) {
      clearNavHideTimer();
      setIsNavRevealed(true);
    } else if (!isNavHoveredRef.current && !isMenuOpenRef.current && !openNavDropdownRef.current) {
      scheduleHideNavAfterIdle();
    }
  }, [isLangMenuOpen, isNavAutoHide, clearNavHideTimer, scheduleHideNavAfterIdle]);

  // Close the language menu when leaving chat or when guided onboarding ends.
  useEffect(() => {
    if (!showGuidedLanguageGlobe) setIsLangMenuOpen(false);
  }, [showGuidedLanguageGlobe]);

  useEffect(() => {
    if (!showGuidedLanguageGlobe) {
      setHasSeenLanguageSelectorCoachMark(true);
      return;
    }
    setHasSeenLanguageSelectorCoachMark(hasSeenLanguageSelectorTutorial(user?.userId));
  }, [showGuidedLanguageGlobe, user?.userId]);

  const dismissLanguageSelectorTutorial = useCallback(() => {
    markLanguageSelectorTutorialSeen(user?.userId);
    setHasSeenLanguageSelectorCoachMark(true);
  }, [user?.userId]);

  const showLanguageSelectorTutorial = shouldShowLanguageSelectorTutorial({
    showGuidedLanguageGlobe,
    isMobileViewport: isLanguageSelectorTutorialMobileViewport,
    hasSeenTutorial: hasSeenLanguageSelectorCoachMark,
  });
  const showCustomerWordmark = shouldShowCustomerWordmark({
    isBsideMode,
    isNavShrunk,
    isMobileViewport: isLanguageSelectorTutorialMobileViewport,
  });
  const getLanguageSelectorTutorialTarget = useCallback(() => langAnchorRef.current, []);
  const languageSelectorTutorialTargetRect = useDynamicCoachMarkTargetRect({
    enabled: showLanguageSelectorTutorial,
    getTarget: getLanguageSelectorTutorialTarget,
    refreshDeps: [isNavShrunk, isNavRevealed],
  });

  useEffect(() => {
    if (showLanguageSelectorTutorial) {
      clearNavHideTimer();
      setIsNavRevealed(true);
    }
  }, [clearNavHideTimer, showLanguageSelectorTutorial]);

  const clearLangCloseTimer = useCallback(() => {
    if (langCloseTimerRef.current != null) {
      window.clearTimeout(langCloseTimerRef.current);
      langCloseTimerRef.current = null;
    }
  }, []);

  const handleLangMenuEnter = useCallback(() => {
    if (showLanguageSelectorTutorial) {
      dismissLanguageSelectorTutorial();
    }
    clearLangCloseTimer();
    setOpenNavDropdown(null);
    setIsLangMenuOpen(true);
  }, [clearLangCloseTimer, dismissLanguageSelectorTutorial, showLanguageSelectorTutorial]);

  const handleLangMenuLeave = useCallback(() => {
    clearLangCloseTimer();
    langCloseTimerRef.current = window.setTimeout(() => {
      langCloseTimerRef.current = null;
      setIsLangMenuOpen(false);
    }, NAV_DROPDOWN_CLOSE_DELAY_MS);
  }, [clearLangCloseTimer]);

  useLayoutEffect(() => {
    if (!isLangMenuOpen) return undefined;
    const measure = () => setLangBridgePx(getDropdownBridgePx(langAnchorRef.current, NAV_DROPDOWN_GAP_PX));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isLangMenuOpen]);

  const handleLangMenuToggle = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (showLanguageSelectorTutorial) {
      dismissLanguageSelectorTutorial();
    }
    clearLangCloseTimer();
    setIsLangMenuOpen(prev => !prev);
  }, [clearLangCloseTimer, dismissLanguageSelectorTutorial, showLanguageSelectorTutorial]);

  useEffect(() => () => clearLangCloseTimer(), [clearLangCloseTimer]);

  const handleSelectLanguage = useCallback((code: SmartSearchGuidedLanguageCode) => {
    if (showLanguageSelectorTutorial) {
      dismissLanguageSelectorTutorial();
    }
    setSelectedLanguage(code);
    if (typeof document !== 'undefined') {
      document.body.dataset.smartSearchLanguage = code;
    }
    window.dispatchEvent(new CustomEvent('smartsearch:setlanguage', { detail: { code } }));
    clearLangCloseTimer();
    setIsLangMenuOpen(false);
  }, [clearLangCloseTimer, dismissLanguageSelectorTutorial, showLanguageSelectorTutorial]);

  useEffect(() => {
    clearNavHideTimer();
    if (revealRafRef.current != null) {
      window.cancelAnimationFrame(revealRafRef.current);
      revealRafRef.current = null;
    }
    if (isNavSmartRevealActive) {
      setIsNavRevealed(false);
      return;
    }

    setIsNavRevealed(true);
  }, [clearNavHideTimer, isNavSmartRevealActive, location.pathname, isSmartSearchChatMode]);

  useEffect(() => {
    if (!isNavAutoHide) return;
    const triggerReveal = () => {
      if (revealRafRef.current != null) return;
      revealRafRef.current = window.requestAnimationFrame(() => {
        revealRafRef.current = null;
        revealNav();
      });
    };

    // On the chat route the page itself doesn't scroll — the chat container does.
    // Prefer that container for reveal so scrolling inner widgets
    // (dropdown listboxes, menus, etc.) never pops the bar back into view.
    // Focused pre-chat / non-scrollable panes use upward wheel/touch intent instead.
    if (isChatRoute) {
      let lastTouchY: number | null = null;
      const onChatScroll = (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (!target || typeof target.matches !== 'function') return;
        if (!target.matches('[data-smartsearch-scroll-root="true"]')) return;
        triggerReveal();
      };
      const shouldUseGestureReveal = () => {
        if (isSmartSearchFocusedMode) return true;
        const scrollRoot = document.querySelector(
          '[data-smartsearch-scroll-root="true"]'
        ) as HTMLElement | null;
        if (!scrollRoot) return true;
        return scrollRoot.scrollHeight <= scrollRoot.clientHeight + 1;
      };
      const onWheelReveal = (event: WheelEvent) => {
        if (!shouldUseGestureReveal()) return;
        // Wheel up (negative deltaY) = "scroll up" intent even when content can't move.
        if (event.deltaY >= 0) return;
        triggerReveal();
      };
      const onTouchStart = (event: TouchEvent) => {
        lastTouchY = event.touches[0]?.clientY ?? null;
      };
      const onTouchMoveReveal = (event: TouchEvent) => {
        if (!shouldUseGestureReveal()) return;
        const currentY = event.touches[0]?.clientY;
        if (currentY == null || lastTouchY == null) return;
        const deltaY = currentY - lastTouchY;
        lastTouchY = currentY;
        // Finger moving down means content would scroll up.
        if (deltaY <= 8) return;
        triggerReveal();
      };
      const onTouchEnd = () => {
        lastTouchY = null;
      };
      document.addEventListener('scroll', onChatScroll, {
        passive: true,
        capture: true,
      } as any);
      window.addEventListener('wheel', onWheelReveal, {
        passive: true,
      });
      window.addEventListener('touchstart', onTouchStart, {
        passive: true,
      });
      window.addEventListener('touchmove', onTouchMoveReveal, {
        passive: true,
      });
      window.addEventListener('touchend', onTouchEnd, {
        passive: true,
      });
      window.addEventListener('touchcancel', onTouchEnd, {
        passive: true,
      });
      return () => {
        document.removeEventListener('scroll', onChatScroll, { capture: true } as any);
        window.removeEventListener('wheel', onWheelReveal as EventListener);
        window.removeEventListener('touchstart', onTouchStart as EventListener);
        window.removeEventListener('touchmove', onTouchMoveReveal as EventListener);
        window.removeEventListener('touchend', onTouchEnd as EventListener);
        window.removeEventListener('touchcancel', onTouchEnd as EventListener);
        if (revealRafRef.current != null) {
          window.cancelAnimationFrame(revealRafRef.current);
          revealRafRef.current = null;
        }
      };
    }

    document.addEventListener('scroll', triggerReveal, {
      passive: true,
      capture: true,
    } as any);
    window.addEventListener('wheel', triggerReveal, {
      passive: true,
    });
    window.addEventListener('touchmove', triggerReveal, {
      passive: true,
    });
    return () => {
      document.removeEventListener('scroll', triggerReveal, {
        capture: true,
      } as any);
      window.removeEventListener('wheel', triggerReveal as EventListener);
      window.removeEventListener('touchmove', triggerReveal as EventListener);
      if (revealRafRef.current != null) {
        window.cancelAnimationFrame(revealRafRef.current);
        revealRafRef.current = null;
      }
    };
  }, [isNavAutoHide, isChatRoute, isSmartSearchFocusedMode, revealNav]);

  useEffect(() => {
    return () => {
      clearNavHideTimer();
      if (revealRafRef.current != null) {
        window.cancelAnimationFrame(revealRafRef.current);
        revealRafRef.current = null;
      }
    };
  }, [clearNavHideTimer]);

  // Collapse the bar into the centered pill once the user scrolls down.
  // Chat listens to the main chat scroller; home listens to the landing scroller.
  useEffect(() => {
    if ((!isChatRoute && !isLandingRoute) || isSmartSearchFocusedMode) {
      setIsChatPageScrolled(false);
      setIsHomePageScrolled(false);
      return;
    }
    const SHRINK_THRESHOLD_PX = 16;
    let raf: number | null = null;
    const onScroll = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || typeof target.matches !== 'function') return;

      let scrollTop: number | null = null;
      if (isChatRoute && target.matches('[data-smartsearch-scroll-root="true"]')) {
        scrollTop = target.scrollTop;
      } else if (isLandingRoute && target.matches('[data-landing-scroll-root="true"]')) {
        if (target.dataset.landingNavShrinkEnabled !== 'true') {
          setIsHomePageScrolled(false);
          return;
        }
        scrollTop = target.scrollTop;
      } else {
        return;
      }

      if (raf != null) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        const next = scrollTop! > SHRINK_THRESHOLD_PX;
        if (isChatRoute) {
          setIsChatPageScrolled(prev => (prev === next ? prev : next));
        }
        if (isLandingRoute) {
          setIsHomePageScrolled(prev => (prev === next ? prev : next));
        }
      });
    };
    document.addEventListener('scroll', onScroll, { passive: true, capture: true } as any);
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true } as any);
      if (raf != null) window.cancelAnimationFrame(raf);
    };
  }, [isChatRoute, isLandingRoute, isSmartSearchFocusedMode]);

  // Chat page: collapse the bar into the centered pill while the profile side panel is expanded.
  useEffect(() => {
    const readDataset = () => document.body.dataset.smartSearchProfileExpanded === 'true';
    const sync = (expanded?: boolean) => {
      if (!isChatRoute) {
        setIsProfilePanelExpanded(false);
        return;
      }
      setIsProfilePanelExpanded(Boolean(expanded ?? readDataset()));
    };
    sync();
    const handler = (event: Event) => {
      const ce = event as CustomEvent<{ expanded?: boolean }>;
      sync(ce.detail?.expanded);
    };
    window.addEventListener('smartsearch:profilepanel', handler as EventListener);
    return () => {
      window.removeEventListener('smartsearch:profilepanel', handler as EventListener);
    };
  }, [isChatRoute]);

  // Chat page: keep the full bar visible until shrink/focused idle hide starts.
  // After shrink, show the pill briefly then auto-hide. Focused pre-chat has no
  // shrink, so start the same idle hide + upward-gesture reveal loop there.
  useEffect(() => {
    if (!isChatRoute) return;
    if (isFocusedPreChatAutoHide) {
      setIsNavRevealed(true);
      scheduleHideNavAfterIdle();
      return;
    }
    if (isChatShrunk) {
      setIsNavRevealed(true);
      scheduleHideNavAfterIdle();
    } else {
      clearNavHideTimer();
      setIsNavRevealed(true);
    }
  }, [
    isChatRoute,
    isChatShrunk,
    isFocusedPreChatAutoHide,
    scheduleHideNavAfterIdle,
    clearNavHideTimer,
  ]);

  const isSignedIn = isAuthenticated && Boolean(user);
  const avatarInitials = resolveSignedInAvatarInitials(profile, user);

  /*
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  */
  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  const handleLogout = async () => {
    handleUserMenuClose();
    void dispatch(logout());
    navigate('/login');
  };
  const handlePersonalCenter = () => {
    handleUserMenuClose();
    navigate(isBsideMode ? '/business/settings' : '/settings');
  };
  const handleNameCard = () => {
    handleUserMenuClose();
    setShowNameCardOverlay(true);
  };
  const handleSignIn = () => {
    handleUserMenuClose();
    // Homepage sign-in returns home. Keep Smart Search/chat and all other
    // entry points on their existing authentication/navigation flow.
    if (isLandingRoute) {
      navigate('/login', { state: { from: location } });
    } else {
      navigate('/login');
    }
  };
  const handleSignInAsAgent = () => {
    handleUserMenuClose();
    navigate('/business/login');
  };
  const signedInSwitchAccountConfig = getSignedInSwitchAccountConfig(isBsideMode);
  const handleSignedInSwitchAccount = () => {
    handleUserMenuClose();
    navigate(signedInSwitchAccountConfig.targetPath);
  };
  const handleBrandNavigate = () => {
    navigate(getBrandNavigateTarget(isBsideMode));
  };
  const handleSmartSearchNavigate = () => {
    trackUiEvent('main_button_click', 'navbar_desktop', 'smart_search');
    trackEvent('nav_smart_search_click', {
      source: 'app_navbar',
      current_path: location.pathname,
    });
    if (location.pathname === '/chat-with-mimi') {
      window.location.href = '/chat-with-mimi';
    } else {
      navigate('/chat-with-mimi');
    }
  };

  const tabQuery = new URLSearchParams(location.search).get('tab');
  const [openNavDropdown, setOpenNavDropdown] = useState<NavDropdownId | null>(null);
  openNavDropdownRef.current = openNavDropdown;
  const [hoveredNavItem, setHoveredNavItem] = useState<NavItemId | null>(null);
  const navLinksLeaveTimerRef = useRef<number | null>(null);
  const focusedNavItem: NavItemId | null = hoveredNavItem;

  const clearNavLinksLeaveTimer = useCallback(() => {
    if (navLinksLeaveTimerRef.current != null) {
      window.clearTimeout(navLinksLeaveTimerRef.current);
      navLinksLeaveTimerRef.current = null;
    }
  }, []);

  const handleNavItemHover = useCallback(
    (id: NavItemId | null) => {
      clearNavLinksLeaveTimer();
      setHoveredNavItem(id);
    },
    [clearNavLinksLeaveTimer]
  );

  const handleNavLinksMouseLeave = useCallback(() => {
    clearNavLinksLeaveTimer();
    navLinksLeaveTimerRef.current = window.setTimeout(() => {
      navLinksLeaveTimerRef.current = null;
      setHoveredNavItem(null);
      // Leaving the links row (or the dropdown panel) closes the dropdown. Gaps between labels
      // stay inside the row, so hover can travel label → label and the panel slides across.
      setOpenNavDropdown(null);
    }, NAV_DROPDOWN_CLOSE_DELAY_MS);
  }, [clearNavLinksLeaveTimer]);

  useEffect(() => {
    return () => {
      clearNavLinksLeaveTimer();
    };
  }, [clearNavLinksLeaveTimer]);

  useEffect(() => {
    if (!isNavAutoHide) return;
    if (openNavDropdown) {
      clearNavHideTimer();
      setIsNavRevealed(true);
      return;
    }
    if (!isNavHoveredRef.current && !isMenuOpenRef.current) {
      scheduleHideNavAfterIdle();
    }
  }, [openNavDropdown, isNavAutoHide, clearNavHideTimer, scheduleHideNavAfterIdle]);

  const handleNavDropdownEnter = useCallback((id: NavDropdownId) => {
    handleNavItemHover(id);
    setOpenNavDropdown(id);
  }, [handleNavItemHover]);

  const handleNavDropdownItemSelect = useCallback(() => {
    clearNavLinksLeaveTimer();
    setOpenNavDropdown(null);
  }, [clearNavLinksLeaveTimer]);

  const isHomeCompareActive = location.pathname.startsWith('/comparison') && tabQuery !== 'saved';
  const isSavedHomesActive =
    (location.pathname.startsWith('/comparison') || location.pathname.startsWith('/activity')) && tabQuery === 'saved';
  const isLivingPreferenceActive = location.pathname.startsWith('/living-preference');
  const isMyScheduleActive =
    location.pathname.startsWith('/activity') && tabQuery === 'schedule';
  const isMyApplicationActive =
    location.pathname.startsWith('/activity') && tabQuery === 'application';
  const isRenterToolkitActive = location.pathname.startsWith('/renter-toolkit');

  const showAuthNavLinks = !isBsideMode && isAuthenticated;

  const navDropdownMenus: Record<NavDropdownId, NavDropdownItemDef[]> = {
    compare: [
      {
        label: 'Home Compare',
        onClick: () => navigate('/comparison'),
        isActive: isHomeCompareActive,
      },
      {
        label: 'Saved Homes',
        onClick: () => navigate('/comparison?tab=saved'),
        isActive: isSavedHomesActive,
      },
      // Disabled extra entry (was a panel child): 'Previous review' → navigate('/comparison/review').
    ],
    'my-journey': [
      {
        label: 'Profile',
        onClick: () => navigate('/living-preference'),
        isActive: isLivingPreferenceActive,
      },
      {
        label: 'My Schedule',
        onClick: () => navigate('/activity?tab=schedule'),
        isActive: isMyScheduleActive,
      },
      {
        label: 'My Application',
        onClick: () => navigate('/activity?tab=application'),
        isActive: isMyApplicationActive,
      },
      {
        label: 'Renter Toolkit',
        onClick: () => navigate('/renter-toolkit'),
        isActive: isRenterToolkitActive,
      },
      // Disabled extra entry (was a panel child): 'Lease / Signing' → navigate('/lease-signing').
    ],
    'bside-offer': [
      {
        label: 'Pending',
        onClick: () => navigate('/business/offer?tab=pending'),
        isActive: location.pathname.startsWith('/business/offer') && tabQuery !== 'complete',
      },
      {
        label: 'Complete',
        onClick: () => navigate('/business/offer?tab=complete'),
        isActive: location.pathname.startsWith('/business/offer') && tabQuery === 'complete',
      },
    ],
  };

  const smartSearchLabelButtonSx = {
    p: 0,
    minWidth: 'auto',
    textTransform: 'none' as const,
    whiteSpace: 'nowrap' as const,
    ...navLinkLabelDefaultTypographySx,
    ...navLinkLabelTypographyTransitionSx,
    background:
      'linear-gradient(-84.57deg, #EE5D05 25.63%, #FE8AB5 41.49%, #31A4CB 71.39%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundColor: 'transparent',
    '&:hover, &:focus-visible': {
      backgroundColor: 'transparent',
      background:
        'linear-gradient(-84.57deg, #EE5D05 25.63%, #FE8AB5 41.49%, #31A4CB 71.39%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
  };

  const renderSmartSearchLabel = () => (
    <Box
      ref={getNavItemAnchorRef('smart-search')}
      onMouseEnter={() => {
        handleNavItemHover('smart-search');
        setOpenNavDropdown(null);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        ...getNavItemDimSx('smart-search', focusedNavItem),
      }}
    >
      <Button
        onClick={handleSmartSearchNavigate}
        disableRipple
        className="nav-bar-link-label"
        sx={smartSearchLabelButtonSx}
      >
        Smart Search
      </Button>
    </Box>
  );

  const renderBsideNavItems = () => (
    <>
      {BSIDE_NAV_ITEMS.map(item => {
        if (item.id === 'bside-offer') {
          return (
            <NavHoverDropdown
              key={item.id}
              dropdownId="bside-offer"
              navItemId="bside-offer"
              focusedNavItem={focusedNavItem}
              open={openNavDropdown === 'bside-offer'}
              onDropdownEnter={handleNavDropdownEnter}
              anchorRef={getNavItemAnchorRef('bside-offer')}
              label={item.label}
              onLabelClick={() => navigate(item.path)}
              withHoverMotion
            />
          );
        }

        return (
          <Box
            key={item.id}
            onMouseEnter={() => {
              handleNavItemHover(item.id);
              setOpenNavDropdown(null);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              ...navLinkHoverMotionSx,
              ...getNavItemDimSx(item.id, focusedNavItem),
            }}
          >
            <Button
              onClick={() => navigate(item.path)}
              disableRipple
              className="nav-bar-link-label"
              sx={{
                ...navLinkLabelButtonSx,
                fontWeight: 400,
                fontSize: scalePx(18),
                color: '#363434',
                '&:hover, &:focus-visible': {
                  backgroundColor: 'transparent',
                  color: '#363434',
                  ...navLinkLabelHoverTypographySx,
                },
              }}
            >
              {item.label}
            </Button>
          </Box>
        );
      })}
    </>
  );

  /*
  const csideNavItems: NavItem[] = [
    { text: 'Home', path: '/' },
    // About Us hidden for now — restore with /about route in App.tsx
    // {
    //   text: 'About Us',
    //   path: '/about',
    //   match: pathname => pathname === '/about',
    // },
    { text: 'Smart Search', path: '/chat-with-mimi' },
  ];

  const bsideNavItems: NavItem[] = BSIDE_NAV_ITEMS.map(item => ({
    text: item.label,
    path: item.path,
    match: item.match,
  }));
  const sidebarNavItems: NavItem[] = isBsideMode ? bsideNavItems : csideNavItems;

  const drawer = (
    <Box
      sx={{
        width: {
          xs: '100vw',
          md: '1104px',
        },
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        pt: {
          xs: '120px',
          md: '274px',
        },
        pb: {
          xs: '40px',
          md: '80px',
        },
        px: {
          xs: '24px',
          md: '73px',
        },
        gap: '29px',
        alignItems: 'flex-start',
        boxSizing: 'border-box',
      }}
      role="presentation"
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backdropFilter: 'blur(10px)',
          WebkitMask: 'linear-gradient(90deg, rgba(0,0,0,1) 34.66%, rgba(0,0,0,0) 99.93%)',
          mask: 'linear-gradient(90deg, rgba(0,0,0,1) 34.66%, rgba(0,0,0,0) 99.93%)',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      />

      <IconButton
        onClick={() => setMobileOpen(false)}
        sx={{
          position: 'absolute',
          top: '30px',
          left: {
            xs: '24px',
            md: '73px',
          },
          color: '#363434',
          p: 0.5,
          zIndex: 2,
          '&:hover': {
            backgroundColor: 'rgba(57,57,57,0.1)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {sidebarNavItems.map(item => {
        const isSmartSearchItem = !isBsideMode && item.path === '/chat-with-mimi';
        const isItemActive = !isBsideMode
          ? (item.match?.(location.pathname) ??
            (item.path === '/'
              ? location.pathname === '/'
              : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)))
          : false;
        return (
          <Button
            key={item.text}
            onClick={() => {
              if (!isBsideMode && item.path === '/chat-with-mimi') {
                trackUiEvent('main_button_click', 'navbar_mobile', 'smart_search');
                trackEvent('nav_smart_search_click', {
                  source: 'app_navbar',
                  current_path: location.pathname,
                });
              }
              if (item.external) {
                window.location.href = item.path;
              } else {
                if (!isBsideMode && item.path === '/chat-with-mimi' && location.pathname === '/chat-with-mimi') {
                  window.location.href = '/chat-with-mimi';
                } else {
                  navigate(item.path);
                }
              }
              setMobileOpen(false);
            }}
            disableRipple
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 'var(--type-h2)',
              fontStyle: 'normal',
              fontWeight: isItemActive ? 800 : 500,
              lineHeight: 'normal',
              textTransform: 'none',
              color: '#363434',
              padding: 0,
              minWidth: 'auto',
              justifyContent: 'flex-start',
              zIndex: 1,
              ...(isSmartSearchItem && {
                background: 'linear-gradient(271deg, #FF6200 45.02%, #EF99B9 69.36%, #3BCDFD 99.63%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }),
              '&:hover': {
                backgroundColor: 'transparent',
                opacity: 0.8,
              },
            }}
          >
            {item.text}
          </Button>
        );
      })}

      <Box sx={{ flexGrow: 1 }} />

      {!isBsideMode && (
        <Button
          onClick={() => {
            navigate('/business/login');
            setMobileOpen(false);
          }}
          disableRipple
          sx={{
            display: 'flex',
            width: {
              xs: '100%',
              sm: '189px',
            },
            maxWidth: '189px',
            height: '46px',
            padding: '8px 20px 7px 20px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            flexShrink: 0,
            borderRadius: '10px',
            background: '#F1EEED',
            boxShadow: '0 0 3.4px 0 #898989, 0 0 4px 0 rgba(245, 245, 245, 0.50) inset',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'var(--type-body)',
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.5px',
            textTransform: 'none',
            color: '#363434',
            zIndex: 1,
            '&:hover': {
              background: '#E9E3E0',
              boxShadow: '0 0 9px 2px rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          Sign In as Agent
        </Button>
      )}
    </Box>
  );
  */

  const renderLanguageSelectorTutorial = () => {
    if (!showLanguageSelectorTutorial || !languageSelectorTutorialTargetRect) return null;

    const rect = languageSelectorTutorialTargetRect;
    const spotlightPadding = 9;
    const tooltipWidth = 300;
    const tooltipLeft = Math.max(
      16,
      Math.min(rect.left - tooltipWidth + rect.width + 14, window.innerWidth - tooltipWidth - 16)
    );
    const tooltipTop = rect.bottom + 20;
    const coachCopy = getSmartSearchGuidedCopy(selectedLanguage).languageSelectorTutorial;

    return (
      <Box
        aria-live="polite"
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1500,
          pointerEvents: 'none',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            left: `${rect.left - spotlightPadding}px`,
            top: `${rect.top - spotlightPadding}px`,
            width: `${rect.width + spotlightPadding * 2}px`,
            height: `${rect.height + spotlightPadding * 2}px`,
            borderRadius: '999px',
            border: '2px solid rgba(255, 113, 25, 0.92)',
            boxShadow: '0 0 0 9999px rgba(31, 27, 25, 0.46), 0 0 0 7px rgba(255, 113, 25, 0.14), 0 16px 36px rgba(255, 113, 25, 0.24)',
            animation: 'smartSearchLanguageTutorialPulse 1.55s ease-in-out infinite',
            '@keyframes smartSearchLanguageTutorialPulse': {
              '0%, 100%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 9999px rgba(31, 27, 25, 0.46), 0 0 0 7px rgba(255, 113, 25, 0.14), 0 16px 36px rgba(255, 113, 25, 0.24)',
              },
              '50%': {
                transform: 'scale(1.045)',
                boxShadow: '0 0 0 9999px rgba(31, 27, 25, 0.46), 0 0 0 12px rgba(255, 113, 25, 0.1), 0 18px 44px rgba(255, 113, 25, 0.28)',
              },
            },
          }}
        />
        <Paper
          elevation={0}
          role="dialog"
          aria-label={coachCopy.eyebrow}
          sx={{
            position: 'fixed',
            left: `${tooltipLeft}px`,
            top: `${tooltipTop}px`,
            width: tooltipWidth,
            maxWidth: 'calc(100vw - 32px)',
            pointerEvents: 'auto',
            borderRadius: '18px',
            p: 2.25,
            bgcolor: 'rgba(255, 250, 247, 0.97)',
            border: '1px solid rgba(255, 113, 25, 0.28)',
            boxShadow: '0 24px 70px rgba(31, 27, 25, 0.24)',
            fontFamily: "'Montserrat', sans-serif",
            '&::before': {
              content: '""',
              position: 'absolute',
              right: 26,
              top: -8,
              width: 16,
              height: 16,
              bgcolor: 'rgba(255, 250, 247, 0.97)',
              borderLeft: '1px solid rgba(255, 113, 25, 0.28)',
              borderTop: '1px solid rgba(255, 113, 25, 0.28)',
              transform: 'rotate(45deg)',
            },
          }}
        >
          <Box sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            color: '#FF7119',
            textTransform: 'uppercase',
            letterSpacing: 0,
            mb: 0.8,
          }}
          >
            {coachCopy.eyebrow}
          </Box>
          <Box sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.35,
            color: '#363434',
            mb: 0.8,
          }}
          >
            {coachCopy.title}
          </Box>
          <Box sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 13.5,
            lineHeight: 1.55,
            color: 'rgba(54, 52, 52, 0.72)',
            mb: 1.6,
          }}
          >
            {coachCopy.body}
          </Box>
          <Button
            variant="contained"
            onClick={dismissLanguageSelectorTutorial}
            sx={{
              height: 34,
              px: 2,
              borderRadius: '999px',
              bgcolor: '#FF7119',
              boxShadow: '0 10px 20px rgba(255, 113, 25, 0.2)',
              color: '#fff',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#E85F0E',
                boxShadow: '0 12px 24px rgba(255, 113, 25, 0.24)',
              },
            }}
          >
            {coachCopy.dismiss}
          </Button>
        </Paper>
      </Box>
    );
  };

  const shouldHideNavForLandingIntro =
    isLandingRoute &&
    (isLandingIntroVisible ||
      (typeof document !== 'undefined' && document.body.dataset.landingIntroVisible !== 'false'));

  return (
    <>
      {!shouldHideNavForLandingIntro && (
      <Box
        ref={navFrameRef}
        onMouseEnter={isNavAutoHide ? handleNavMouseEnter : undefined}
        onMouseLeave={isNavAutoHide ? handleNavMouseLeave : undefined}
        sx={{
          position: 'relative',
          zIndex: (theme: any) => theme.zIndex.appBar,
          backgroundColor: 'transparent',
          ...(isNavFixed
            ? {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                zIndex: (theme: any) => theme.zIndex.appBar,
                transform:
                  isNavAutoHide && !isNavRevealed
                    ? 'translate3d(0, -110%, 0)'
                    : 'translate3d(0, 0, 0)',
                transition: 'transform 240ms ease',
                willChange: 'transform',
                pointerEvents: isNavAutoHide && !isNavRevealed ? 'none' : 'auto',
              }
            : {}),
          flexShrink: 0,
        }}
      >
        <AppBar
          ref={appBarRef}
          position="static"
          elevation={0}
          sx={{
            backgroundColor: 'transparent !important',
            backgroundImage: 'none !important',
            backdropFilter: 'none !important',
            WebkitBackdropFilter: 'none !important',
            boxShadow: 'none !important',
            borderBottom: 'none !important',
            color: '#393939',
            '&.MuiPaper-root': {
              boxShadow: 'none !important',
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              pt: { xs: '10px', md: isBsideMode ? '20px' : '12px' },
              pb: 0,
              pl: { xs: '12px', md: isBsideMode ? '20px' : (showCustomerWordmark ? '7.5px' : '15px') },
              pr: { xs: '12px', md: isBsideMode ? '9px' : (showCustomerWordmark ? '7.5px' : '15px') },
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              {...{ [NAV_BAR_PILL_ATTR]: '' }}
              sx={{
                boxSizing: 'border-box',
                // Keep width animatable. `fit-content` snaps from 100% on shrink,
                // which made scroll-down feel much faster than scroll-up expansion.
                width: '100%',
                maxWidth: {
                  xs: 'calc(100vw - 24px)',
                  md: isBsideMode
                    ? `${BSIDE_NAV_MAX_WIDTH_PX}px`
                    : `${getCustomerNavMaxWidth({
                      isNavShrunk,
                      showGuidedLanguageGlobe,
                      showGuestLoginButton,
                    })}px`,
                },
                flexShrink: 0,
                minHeight: { xs: scalePx(52).xs, md: isBsideMode ? '58px' : scalePx(52).md },
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: { xs: scalePx(10).xs, md: isBsideMode ? '10px' : scalePx(10).md },
                pl: { xs: scalePx(15).xs, md: '15px' },
                pr: { xs: scalePx(8).xs, md: isBsideMode || showCustomerWordmark ? '4px' : scalePx(8).md },
                py: { xs: scalePx(8).xs, md: isBsideMode || showCustomerWordmark ? '10px' : scalePx(8).md },
                borderRadius: { xs: scalePx(20).xs, md: '20px' },
                border: isBsideMode || !isNavShrunk ? '0.5px solid #D3CCC9' : 'none',
                backgroundColor: 'rgba(211, 204, 201, 0.2)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow: isBsideMode || !isNavShrunk ? '0px 4px 6px 0px rgba(0, 0, 0, 0.05)' : 'none',
                overflow: 'hidden',
                transition: 'width 360ms cubic-bezier(0.22, 1, 0.36, 1), max-width 360ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              {!isBsideMode && (
                <NavHoverPill hoveredId={hoveredNavItem} anchorsRef={navItemAnchorsRef} layoutKey={isNavShrunk} />
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  gap: {
                    xs: scalePx(10).xs,
                    md: isBsideMode ? '11px' : (showCustomerWordmark ? '11px' : scalePx(10).md),
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: { xs: scalePx(45.294).xs, md: isBsideMode || showCustomerWordmark ? '34px' : scalePx(45.294).md },
                    height: { xs: scalePx(43.833).xs, md: isBsideMode || showCustomerWordmark ? '33px' : scalePx(43.833).md },
                  }}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label="Go to home page"
                    onClick={handleBrandNavigate}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleBrandNavigate();
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: { xs: scalePx(45.294).xs, md: isBsideMode || showCustomerWordmark ? '34px' : scalePx(45.294).md },
                      height: { xs: scalePx(43.833).xs, md: isBsideMode || showCustomerWordmark ? '33px' : scalePx(43.833).md },
                      cursor: 'pointer',
                      outline: 'none',
                      borderRadius: isBsideMode ? '6px' : 0,
                      overflow: isBsideMode ? 'hidden' : 'visible',
                      '&:focus-visible': {
                        outline: '2px solid rgba(57, 57, 57, 0.35)',
                        outlineOffset: '4px',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/allroomie-mark.svg"
                      alt=""
                      aria-hidden="true"
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </Box>
                  {/* Previous combined Hooli + Henry Zhao logos intentionally disabled. */}
                  {/*
                  <Box component="img" src="/assets/hooli.png" alt="" aria-hidden="true" />
                  <Box component="svg" viewBox="0 0 1 17" aria-hidden="true">
                    <path d="M0.5 0.5V16.5" stroke="#393939" strokeLinecap="round" />
                  </Box>
                  <Box component="img" src="/assets/henry_sig.png" alt="" aria-hidden="true" />
                  */}
                </Box>

                {showCustomerWordmark && (
                  <Box
                    component="span"
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      color: '#363434',
                      fontFamily: "'Alexandria', 'Montserrat', sans-serif",
                      fontSize: '18px',
                      fontWeight: 700,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    AllRoomie
                  </Box>
                )}

                {/*
                {!isChatShrunk && (
                  <SidebarButton
                    onClick={handleDrawerToggle}
                    sx={{
                      width: scalePx(25.898),
                      height: scalePx(26),
                    }}
                  />
                )}
                */}
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: {
                  xs: toPx(10),
                  md: toPx(isBsideMode ? 60 : (isNavShrunk ? 30 : 80)),
                  lg: toPx(isBsideMode ? 60 : (isNavShrunk ? 30 : 80)),
                },
                flexShrink: 0,
                transition: 'gap 360ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}>
                <Box
                  onMouseEnter={clearNavLinksLeaveTimer}
                  onMouseLeave={handleNavLinksMouseLeave}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: {
                      xs: toPx(8),
                      md: toPx(isBsideMode ? 30 : (isNavShrunk ? 20 : 30)),
                      lg: toPx(isBsideMode ? 30 : (isNavShrunk ? 20 : 30)),
                    },
                    transition: 'gap 360ms cubic-bezier(0.22, 1, 0.36, 1)',
                  }}
                >
                  {!isBsideMode && renderSmartSearchLabel()}
                  {isBsideMode && renderBsideNavItems()}

                  {showAuthNavLinks && (
                    <>
                      <NavHoverDropdown
                        dropdownId="compare"
                        navItemId="compare"
                        focusedNavItem={focusedNavItem}
                        open={openNavDropdown === 'compare'}
                        onDropdownEnter={handleNavDropdownEnter}
                        anchorRef={getNavItemAnchorRef('compare')}
                        label={CUSTOMER_COMPARE_NAV_LABEL}
                        onLabelClick={() => navigate('/comparison')}
                      />

                      <NavHoverDropdown
                        dropdownId="my-journey"
                        navItemId="my-journey"
                        focusedNavItem={focusedNavItem}
                        open={openNavDropdown === 'my-journey'}
                        onDropdownEnter={handleNavDropdownEnter}
                        anchorRef={getNavItemAnchorRef('my-journey')}
                        label="Living Hub"
                        onLabelClick={() => navigate('/living-preference')}
                      />
                    </>
                  )}
                </Box>

                {showGuidedLanguageGlobe && (
                  <>
                    <Box
                      ref={langAnchorRef}
                      onMouseEnter={handleLangMenuEnter}
                      onMouseLeave={handleLangMenuLeave}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ...navLinkHoverMotionSx,
                      }}
                    >
                      <IconButton
                        aria-label="Select language"
                        aria-haspopup="true"
                        aria-expanded={isLangMenuOpen}
                        onClick={handleLangMenuToggle}
                        disableRipple
                        sx={{
                          p: 0,
                          color: isLangMenuOpen ? '#e15600' : '#363434',
                          backgroundColor: 'transparent',
                          '&:hover, &:focus-visible': {
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        <LanguageIcon sx={{ width: scalePx(24), height: scalePx(24) }} />
                      </IconButton>
                    </Box>
                    <Popper
                      open={isLangMenuOpen}
                      anchorEl={langAnchorRef.current}
                      placement="bottom"
                      transition
                      sx={{ zIndex: (theme: any) => theme.zIndex.appBar + 1 }}
                      modifiers={[{ name: 'offset', options: { offset: [0, 0] } }]}
                    >
                      {({ TransitionProps }) => (
                        <Box
                          onMouseEnter={handleLangMenuEnter}
                          onMouseLeave={handleLangMenuLeave}
                          sx={{ pt: `${langBridgePx}px` }}
                        >
                          <MotionGrow {...TransitionProps}>
                          <Paper
                            elevation={0}
                            sx={{
                              width: 172,
                              boxSizing: 'border-box',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              gap: '20px',
                              px: '10px',
                              py: '20px',
                              borderRadius: '20px',
                              border: '0.5px solid #D3CCC9',
                              backgroundColor: 'rgba(211, 204, 201, 0.2)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05)',
                            }}
                          >
                            {GUIDED_LANGUAGE_OPTIONS.map(option => {
                              const isActive = option.code === selectedLanguage;
                              return (
                                <Button
                                  key={option.code}
                                  data-motion-item
                                  onClick={() => handleSelectLanguage(option.code)}
                                  disableRipple
                                  sx={{
                                    p: 0,
                                    minWidth: 'auto',
                                    width: '100%',
                                    justifyContent: 'center',
                                    textTransform: 'none',
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: isActive ? 700 : 400,
                                    lineHeight: 'normal',
                                    color: isActive ? '#e15600' : '#363434',
                                    cursor: 'pointer',
                                    '&:hover, &:focus-visible': {
                                      backgroundColor: 'transparent',
                                      color: '#e15600',
                                    },
                                  }}
                                >
                                  {option.label}
                                </Button>
                              );
                            })}
                          </Paper>
                          </MotionGrow>
                        </Box>
                      )}
                    </Popper>
                  </>
                )}

                {showGuestLoginButton ? (
                  <Button
                    type="button"
                    onClick={handleUserMenuClick}
                    disableRipple
                    aria-label="Account menu"
                    aria-haspopup="true"
                    aria-expanded={Boolean(userMenuAnchor)}
                    sx={{
                      ...getGuestLoginButtonSizing(isNavShrunk),
                      fontSize: GUEST_LOGIN_FULL_FONT,
                      py: 0,
                      borderRadius: '20px',
                      background: 'linear-gradient(180deg, #4C403E 0%, #706260 100%)',
                      color: '#FFFFFF',
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 500,
                      lineHeight: 1,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      transition: 'width 360ms cubic-bezier(0.22, 1, 0.36, 1), min-width 360ms cubic-bezier(0.22, 1, 0.36, 1), height 360ms cubic-bezier(0.22, 1, 0.36, 1), padding 360ms cubic-bezier(0.22, 1, 0.36, 1), background 180ms ease',
                      '&:hover, &:focus-visible': {
                        background: 'linear-gradient(180deg, #554844 0%, #786b68 100%)',
                      },
                    }}
                  >
                    <Box component="span" sx={getGuestLoginLabelSx(isNavShrunk)}>Log In</Box>
                  </Button>
                ) : (
                  <IconButton
                    color="inherit"
                    onClick={handleUserMenuClick}
                    aria-label="Account menu"
                    aria-haspopup="true"
                    sx={{ p: 0 }}
                  >
                    <Avatar
                      sx={{
                        width: scalePx(38),
                        height: scalePx(38),
                        fontSize: scalePx(18),
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 500,
                        color: '#FFFFFF',
                        bgcolor: isSignedIn ? '#C36711' : '#A69D9B',
                      }}
                    >
                      {isSignedIn ? avatarInitials : undefined}
                    </Avatar>
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        </AppBar>
        {/* Outside the pill (it clips with overflow: hidden) but inside the fixed/translated
            wrapper, so the panel follows the bar when it shrinks, hides or scrolls. */}
        <NavDropdownPanel
          openId={openNavDropdown}
          menus={navDropdownMenus}
          anchorsRef={navItemAnchorsRef}
          frameRef={navFrameRef}
          layoutKey={isNavShrunk}
          onDropdownEnter={handleNavDropdownEnter}
          onLeave={handleNavLinksMouseLeave}
          onItemSelect={handleNavDropdownItemSelect}
        />
      </Box>
      )}

      {/*
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: {
              xs: '100vw',
              md: '1104px',
            },
            maxWidth: '100vw',
            background: {
              xs: 'rgba(254, 253, 253, 0.97)',
              md: 'linear-gradient(90deg, #FEFDFD 34.66%, rgba(254, 253, 253, 0.00) 99.93%)',
            },
            boxShadow: 'none',
            overflow: 'hidden',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent',
          },
        }}
      >
        {drawer}
      </Drawer>
      */}

      {children}

      {renderLanguageSelectorTutorial()}

      <ProfileDropdownMenu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        signedIn={isSignedIn}
        onSetting={handlePersonalCenter}
        onSignOut={handleLogout}
        onSignIn={handleSignIn}
        onSignInAsAgent={handleSignInAsAgent}
        onNameCard={handleNameCard}
        onSignedInSwitch={handleSignedInSwitchAccount}
        showNameCard={isBsideMode}
        showSignedInSwitchAccount={isBsideMode ? true : undefined}
        signedInSwitchLabel={signedInSwitchAccountConfig.label}
        activeItem={activeProfileMenuItem}
        signInLabel={isBsideRoute ? 'Sign in as tenant' : 'Sign in'}
        showSignInAsAgent={!isBsideMode}
      />
      {showNameCardOverlay && (
        <AgentNameCardPage overlay onClose={() => setShowNameCardOverlay(false)} />
      )}
    </>
  );
};

export default AppNavbar;
